import React, { useState, useEffect, useRef, useReducer, useContext } from 'react';
import { MainContext } from '@blaze-cms/nextjs-components';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { parseUrl } from 'query-string';
import SearchFilter from './SearchFilter';
import { withTitle } from '../../HOC';
import { getSearchPublishedContent, generateSingleItemQuery } from '../../application/query';
import { buildNewUrl, getFilterValues, getFilterRangesAndCheckboxesValues, getUpdatedFilterBy, buildListNameQuery } from './helpers';
import { getEntityData, getInheritedFilters, getQueryProps, buildRawQueryStringified, buildFiltersQuery, getComponentId } from '../../helpers';
import { RAW_RESULTS } from './constants';
import searchFilterReducer from './searchFilterReducer';
import { useGetEntitySchemasAsObj } from '../../hooks';
const SearchFilterContainer = ({
  entity,
  url,
  filters,
  name,
  isCollapsedOnResponsive,
  groupAfterMobile,
  groupAfterDesktop,
  filterBy,
  filterByProperty,
  shouldAddFilters,
  listComponentName,
  parent,
  mobileRefineButtonText
}) => {
  const {
    isPreview
  } = useContext(MainContext);
  const router = useRouter();
  const {
    asPath
  } = router;
  const searchFilterRef = useRef(null);
  const [key, setKey] = useState(`filter-${name}`);
  const [displaySearchFilter, setDisplaySearchFilter] = useState(false);
  const [urlPath, setUrlPath] = useState(asPath); // used as asPath can take too long to update
  const [filterValues, dispatch] = useReducer(searchFilterReducer, null);
  const {
    url: currentUrl,
    query
  } = parseUrl(urlPath);
  const updatedQuery = buildListNameQuery(query, listComponentName);
  const {
    itemId: parentId,
    itemEntity: parentEntity
  } = parent;
  useEffect(() => {
    if (asPath === urlPath) return;
    const {
      query: newQuery
    } = parseUrl(asPath);
    const updatedNewQuery = buildListNameQuery(newQuery, listComponentName);
    const newValues = getFilterValues(filterData, filters, updatedNewQuery);
    if (newValues) dispatch({
      newValues,
      shouldSearch: false,
      type: 'update'
    });
    setUrlPath(asPath);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [asPath]);
  const {
    data: mainSchemas = {},
    loading: schemasLoading
  } = useGetEntitySchemasAsObj([parentEntity, entity]);
  const {
    [parentEntity]: parentSchema = {},
    [entity]: filterEntitySchema = {}
  } = mainSchemas;
  const {
    actions = {}
  } = parentSchema || {};
  const getAction = isPreview ? actions.get : actions.getPublished;
  const inheritedFilters = getInheritedFilters(filterBy, filterByProperty);
  const queryProps = getQueryProps(inheritedFilters, parentSchema, filterEntitySchema);
  const {
    data: {
      entityData: parentData
    } = {},
    loading: parentQueryLoading
  } = useQuery(generateSingleItemQuery(getAction, queryProps), {
    variables: {
      id: parentId
    },
    skip: schemasLoading || !inheritedFilters.length
  });
  const {
    updatedFilterBy,
    updatedFilterByProperty
  } = getUpdatedFilterBy(filterBy, filterByProperty, parentData);
  const action = getSearchPublishedContent(RAW_RESULTS);
  const [checkboxSelectValues, rangeValues] = getFilterRangesAndCheckboxesValues(filters);
  const {
    docType
  } = getEntityData(entity);
  const filtersQuery = buildFiltersQuery({
    shouldAddFilters,
    query: filterValues,
    filterBy: updatedFilterBy,
    filterByProperty: updatedFilterByProperty,
    rangeValues,
    queryKeys: Object.keys(updatedQuery)
  });
  const rawQueryStringified = buildRawQueryStringified(checkboxSelectValues, rangeValues, docType, filtersQuery);
  const {
    data,
    error,
    loading
  } = useQuery(action, {
    variables: {
      rawQueryStringified,
      limit: 0
    },
    // we only want aggs so limit=0 for no search results
    skip: !rawQueryStringified || parentQueryLoading
  });
  if (error) return error.message;
  if (!filters.length) return null;
  const {
    searchPublishedContent: {
      rawResults: {
        aggregations: filterData
      } = {}
    } = {}
  } = data || {};
  if (!filterValues && !loading && !parentQueryLoading && (filterData || !rawQueryStringified)) {
    const newValues = getFilterValues(filterData, filters, updatedQuery);
    dispatch({
      newValues,
      shouldSearch: false,
      type: 'update'
    });
    return null;
  }
  const handleSearch = newQuery => {
    const componentId = listComponentName ? getComponentId(listComponentName) : '';
    setDisplaySearchFilter(false);
    const hashBit = asPath.split('#')[1] || '';
    let parsedHashBit = hashBit ? `#${hashBit}` : '';
    parsedHashBit = parsedHashBit || (componentId ? `#${componentId}` : '');
    scrollToFirstList(componentId);
    if (!newQuery) {
      const baseUrl = url || currentUrl;
      const baseQuery = `${baseUrl}${parsedHashBit}`;
      setUrlPath(baseQuery);
      return router.push('/Resolver', baseQuery, {
        shallow: !url,
        scroll: false
      }).then(() => {
        setKey(`filter-${name}:${Date.now()}`); // remove after range component update
      });
    }
    const hashToUse = componentId;
    const newUrl = buildNewUrl(url, currentUrl, newQuery, hashToUse);
    setUrlPath(newUrl);
    return router.push('/Resolver', newUrl, {
      shallow: !url,
      scroll: false
    });
  };
  const scrollToFirstList = componentId => {
    const list = componentId ? document.getElementById(componentId) : document.getElementsByClassName('list-top')[0];
    const shouldScrollToFirstList = !url && list;
    if (shouldScrollToFirstList) {
      list.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  };
  if (!filterValues) return null;
  return /*#__PURE__*/React.createElement(SearchFilter, {
    key: key,
    setAppliedFilters: dispatch,
    query: updatedQuery,
    listComponentName: listComponentName,
    searchFilterRef: searchFilterRef,
    data: filterData,
    filters: filters,
    hasUrl: !!url,
    entity: entity,
    handleSearch: handleSearch,
    filterValues: filterValues,
    name: name,
    isCollapsedOnResponsive: isCollapsedOnResponsive,
    displaySearchFilter: displaySearchFilter,
    setDisplaySearchFilter: setDisplaySearchFilter,
    groupAfterMobile: groupAfterMobile,
    groupAfterDesktop: groupAfterDesktop,
    mobileRefineButtonText: mobileRefineButtonText
  });
};
SearchFilterContainer.propTypes = {
  mobileRefineButtonText: PropTypes.string,
  entity: PropTypes.string,
  url: PropTypes.string,
  filters: PropTypes.array,
  name: PropTypes.string.isRequired,
  isCollapsedOnResponsive: PropTypes.bool,
  groupAfterMobile: PropTypes.number,
  groupAfterDesktop: PropTypes.number,
  filterBy: PropTypes.array,
  filterByProperty: PropTypes.array,
  shouldAddFilters: PropTypes.bool,
  listComponentName: PropTypes.string,
  parent: PropTypes.object
};
SearchFilterContainer.defaultProps = {
  url: null,
  filters: [],
  entity: '',
  isCollapsedOnResponsive: true,
  groupAfterMobile: 0,
  groupAfterDesktop: 0,
  filterBy: [],
  filterByProperty: [],
  shouldAddFilters: false,
  listComponentName: '',
  parent: {},
  mobileRefineButtonText: ''
};
export default withTitle(SearchFilterContainer);
//# sourceMappingURL=SearchFilterContainer.js.map